<template>
  <div
    class="relative cursor-pointer"
    :class="[{
      'detection-header': !groupingLogo,
      'detection-header-mirrored': !groupingLogo && edge === 'left',
      'detection-grouping-header': groupingLogo
    }]"
    :style="{ height: headerHeight + 'px'}"
    @click="$emit('show-customer', item?.customer ?? null)"
  >
    <!-- LIVE: who took this counter, read by Apodis Live from the LGO
         traffic. Small, in the header's top right corner. -->
    <span
      v-if="item && !detecting && liveOperator"
      class="absolute top-1.5 right-3 flex items-center gap-1 text-[10px] font-medium opacity-80"
      :class="groupingLogo ? 'text-primary' : 'text-secondary'"
      :title="liveOperatorLabel(liveOperator)"
    >
      <UserRound :size="10" aria-hidden="true" />
      {{ liveOperatorShortLabel(liveOperator) }}
    </span>
    <div class="mr-3 flex-shrink-0">
      <img
        v-if="groupingLogo"
        class="h-10 w-10 object-contain"
        :src="groupingLogo"
        alt="Logo Apodis"
      >
      <img
        v-else
        class="h-10 w-10"
        src="@/assets/images/logos/apodis-white.svg"
        alt="Logo Apodis"
      >
    </div>
    <div
      class="flex-1 flex-col text-lg font-black break-normal line-clamp-2"
      :class="[{'text-secondary': !groupingLogo, 'text-primary': groupingLogo, 'pr-12': !!item && !detecting && !!liveOperator}]"
    >
      <div v-if="detecting">
        <!-- The NIR is checksum-validated, and the key is only set when the OCR names
             matched the patient cache. Without either, the names are unverified OCR text. -->
        <div v-if="detecting.nir">
          {{ detecting.nir }}
        </div>
        <div v-else-if="detecting.key">
          {{ ucfirst(detecting.firstName) }} {{ ucfirst(detecting.lastName) }}
        </div>
        <div v-else>
          Détection en cours …
        </div>
      </div>
      <div v-else-if="error && !item">
        {{ error.title }}
      </div>
      <div v-else>
        <div
          v-if="selectedConsumer"
          class="flex-1"
          :class="{'line-clamp-1': alternateConsumer}"
        >
          {{ selectedConsumer.information.first_name }} {{ selectedConsumer.information.last_name }}
        </div>
        <div
          v-else-if="item?.patient"
          class="flex-1"
        >
          {{ item.patient?.firstName }} {{ item.patient?.lastName }}
        </div>
        <div v-else>
          Aucun Patient
        </div>
        <div
          v-if="smokerDeclared"
          class="mt-1 inline-flex items-center gap-1 rounded-xl bg-white/95 px-2 py-0.5 text-xs font-bold text-primary"
        >
          <Icon name="mdi-smoking" :size="13" color="#3513e2" />
          Fumeur
        </div>
        <button
          v-if="alternateConsumer"
          class="flex items-center gap-1 text-xs font-medium opacity-90 cursor-pointer"
          @click.stop="$emit('switch-consumer')"
        >
          <Icon name="mdi-swap-horizontal" :size="14" color="currentColor" />
          <span class="line-clamp-1">
            {{ alternateConsumer.information.first_name }} {{ alternateConsumer.information.last_name }}
            {{ alternateConsumer.type === 'customer' ? '(assuré)' : '(ayant droit)' }}
          </span>
        </button>
      </div>
      <div
        v-if="!!item && !item.certified && (certificationStatus === 'pre-certified' || certificationStatus === 'pending')"
        class="flex flex-row items-center gap-x-2"
      >
        <div class="h-4 w-4 rounded-full object-contain bg-orange-light" />
        <p class="text-xs font-medium h-4">
          {{
            item!.certification_status === 'pre-certified' ? 'En attente de validation patient' : 'En attente de certification'
          }}
        </p>
      </div>
    </div>
    <div v-if="item?.certified" class="bg-white p-2 rounded-full relative">
      <img
        alt="Certifié"
        src="@/assets/icons/certified.svg"
        class="relative"
      >
    </div>
    <div v-if="item && !detecting" class="flex flex-row gap-x-3 justify-around absolute -bottom-5 right-5 text-primary">
      <BaseTooltip avoid-collisions side="bottom">
        <div
          class="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-custom cursor-pointer border-2 border-transparent hover:border-primary/50"
          @click.stop="$emit('navigate-surveys', item.customer ?? null)"
        >
          <span class="font-bold text-xs text-primary">TROD</span>
        </div>
        <template #content>
          <p class="text-sm leading-tight">
            TROD, préremplir un bon, etc.
          </p>
        </template>
      </BaseTooltip>
      <!--      <BaseTooltip avoid-collisions side="bottom">-->
      <!--        <div-->
      <!--          class="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-custom cursor-pointer border-2 border-transparent hover:border-primary/50"-->
      <!--          @click.stop="$emit('navigate-appointment', item.customer ?? null)"-->
      <!--        >-->
      <!--          <span class="font-bold text-sm text-primary">RDV</span>-->
      <!--        </div>-->
      <!--        <template #content>-->
      <!--          <p class="text-sm leading-tight">-->
      <!--            Prendre un rendez-vous-->
      <!--          </p>-->
      <!--        </template>-->
      <!--      </BaseTooltip>-->
      <BaseTooltip v-if="!item.certified" avoid-collisions side="bottom">
        <div
          class="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-custom cursor-pointer border-2 border-transparent hover:border-primary/50"
          @click.stop="$emit('navigate-sms', item.customer ?? null)"
        >
          <span class="font-bold text-sm text-primary">SMS</span>
        </div>
        <template #content>
          <p class="text-sm leading-tight">
            Envoyer un SMS
          </p>
        </template>
      </BaseTooltip>
      <div
        v-if="experimentalFeatures"
        class="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-custom cursor-pointer border-2 border-transparent hover:border-primary/50"
        @click.stop="$emit('navigate-knowledgeables')"
      >
        <AiMenu class="w-6" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import {useSystemStore} from "@/stores/system.store";
import {useNotificationStore} from "@/stores/notification.store";
import {SmokingStatus} from "@/customer/interfaces/DeclaredInformation";
import {mapState} from "pinia";
import {DetectionErrorProps, DetectionNotificationProps, DetectionPatient} from "@/notification/interfaces";
import Consumer from "@/core/interfaces/consumers/Consumer";
import Icon from "@/core/components/icon/Icon.vue";
import AiMenu from "@/core/components/icons/AiMenu.vue";
import BaseTooltip from "@/core/components/tooltips/BaseTooltip.vue";
import {UserRound} from "@lucide/vue";
import {liveOperator, liveOperatorLabel, liveOperatorShortLabel} from "@/live/services/liveOperator";

export const DETECTION_HEADER_HEIGHT = 80;

export default defineComponent({
    name: 'NotificationDetectionCustomerHeader',
    components: {BaseTooltip, AiMenu, Icon, UserRound},
    props: {
        item: {
            type: Object as PropType<DetectionNotificationProps | null>,
            default: null
        },
        detecting: {
            type: Object as PropType<DetectionPatient | null>,
            default: null
        },
        error: {
            type: Object as PropType<DetectionErrorProps | null>,
            default: null
        },
        /** The consumer currently displayed, when a beneficiary was detected. */
        selectedConsumer: {
            type: Object as PropType<Consumer | null>,
            default: null
        },
        /** The other consumer the pharmacist can switch to (beneficiary ↔ insured customer). */
        alternateConsumer: {
            type: Object as PropType<Consumer | null>,
            default: null
        }
    },
    emits: ['navigate-sms', 'navigate-surveys', 'navigate-appointment', 'show-customer', 'navigate-knowledgeables', 'switch-consumer'],
    setup() {
        return {liveOperator, liveOperatorLabel, liveOperatorShortLabel};
    },
    data() {
        return {
            headerHeight: DETECTION_HEADER_HEIGHT,
            experimentalFeatures: false
        };
    },
    computed: {
        ...mapState(useSystemStore, ['environment', 'groupingLogo']),
        ...mapState(useNotificationStore, ['declaredSmoking', 'edge']),
        certificationStatus(): string {
            return this.item!.certification_status;
        },
        /**
         * Whether the displayed consumer was just declared smoker by answering
         * the poll. The detection payload carries no declared information, so
         * the tag acknowledges the answer for the current detection only.
         */
        smokerDeclared(): boolean {
            const key = this.selectedConsumer?.key ?? this.item?.customer?.key;

            return !!key && this.declaredSmoking[key] === SmokingStatus.SMOKER;
        }
    },
    created() {
        window.addEventListener('keydown', this.toggleExperimentalMenu);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.toggleExperimentalMenu);
    },
    methods: {
        /**
         * Toggle experimental menu
         *
         * @param event
         */
        toggleExperimentalMenu(event: KeyboardEvent) {
            if (event.key === 'x' && event.ctrlKey) {
                this.experimentalFeatures = !this.experimentalFeatures;
            }
        },
        ucfirst(string) {
            return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        }
    }
});
</script>
